import { AddSmall } from '@wix/wix-ui-icons-common';
import React, { useMemo, type ReactNode } from 'react';
import {
  DropZone as DropZoneRAC,
  FileTrigger as FileTriggerRAC,
  Button as ButtonRAC,
  Text as TextRAC,
} from 'react-aria-components';

import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { useTranslation } from '../../../contexts/translation-context';
import { findChildOfType } from '../../../services/find-child';
import { type FileUploadProps } from '../file-upload-props';

import { useFileUploadContext } from './file-upload-context';

export type DropZoneProps = {
  children: ReactNode;
  className?: string;
};

export const DropZone = ({ children, className }: DropZoneProps) => {
  const { ariaDescribedBy } = useFieldAttributes();
  const { disabled, hasError } = useFieldPropsV2<FileUploadProps>();
  const { handleFileChange, limitReached } = useFileUploadContext();

  const titleElement = findChildOfType(children, DropZone.Title);
  const subtitleElement = findChildOfType(children, DropZone.Subtitle);
  const buttonElement = findChildOfType(children, DropZone.Button);

  return (
    <DropZoneRAC
      aria-describedby={ariaDescribedBy}
      className={className}
      data-invalid={hasError}
      isDisabled={disabled}
      onDrop={async (e) => {
        const droppedFiles: File[] = [];
        for (const item of e.items) {
          if (item.kind === 'file') {
            const file = await item.getFile();
            if (file) {
              droppedFiles.push(file);
            }
          }
        }
        if (droppedFiles.length > 0) {
          const dataTransfer = new DataTransfer();
          droppedFiles.forEach((file) => dataTransfer.items.add(file));
          handleFileChange(dataTransfer.files);
        }
      }}
    >
      <TextRAC slot="label">
        {titleElement}
        {subtitleElement}
      </TextRAC>

      {!limitReached && buttonElement}
    </DropZoneRAC>
  );
};

export type DropZoneTitleProps = {
  className?: string;
};

export const DropZoneTitle = ({ className }: DropZoneTitleProps) => {
  const { t } = useTranslation();
  const { limitReached } = useFileUploadContext();

  const title = useMemo(
    () =>
      limitReached
        ? t('file-upload.dropzone.limit-reached.title')
        : t('file-upload.dropzone.title'),
    [t, limitReached],
  );

  return <p className={className}>{title}</p>;
};

export type DropZoneSubtitleProps = {
  className?: string;
};

export const DropZoneSubtitle = ({ className }: DropZoneSubtitleProps) => {
  const { t } = useTranslation();
  const { limitReached } = useFileUploadContext();

  const subtitle = useMemo(
    () =>
      limitReached
        ? t('file-upload.dropzone.limit-reached.subtitle')
        : t('file-upload.dropzone.subtitle'),
    [t, limitReached],
  );

  return <p className={className}>{subtitle}</p>;
};

export type DropZoneButtonProps = {
  className?: string;
};

export const DropZoneButton = ({ className }: DropZoneButtonProps) => {
  const { t } = useTranslation();
  const { ariaDescribedBy, inputId, labelId, errorId } = useFieldAttributes();
  const { buttonText, disabled, required, onFocus, hasError, errorMessage } =
    useFieldPropsV2<FileUploadProps>();
  const {
    handleFileChange,
    accept,
    multiple,
    buttonRef,
    handleBlur,
    _error,
    files,
  } = useFileUploadContext();

  const hasFileErrors = files?.some((file) => file.errorMessage) ?? false;
  const hasAnyError = Boolean(
    hasError || errorMessage || _error || hasFileErrors,
  );

  return (
    <FileTriggerRAC
      acceptedFileTypes={accept ? accept.split(',') : undefined}
      allowsMultiple={multiple}
      onSelect={(fileList) => {
        if (fileList) {
          const _files = Array.from(fileList);
          const dataTransfer = new DataTransfer();
          _files.forEach((file) => dataTransfer.items.add(file));
          handleFileChange(dataTransfer.files);
        }
      }}
    >
      <ButtonRAC
        ref={buttonRef}
        id={inputId}
        aria-labelledby={labelId}
        aria-describedby={[ariaDescribedBy, errorId].filter(Boolean).join(' ')}
        aria-required={required}
        aria-invalid={hasAnyError}
        aria-roledescription={t('file-upload.aria-roledescription')}
        isDisabled={disabled}
        className={className}
        onFocus={onFocus}
        onBlur={handleBlur}
      >
        <AddSmall aria-hidden="true" />

        {buttonText}
      </ButtonRAC>
    </FileTriggerRAC>
  );
};

DropZone.Title = DropZoneTitle;
DropZone.Subtitle = DropZoneSubtitle;
DropZone.Button = DropZoneButton;
